import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { faClock, faFileContract } from '@fortawesome/pro-regular-svg-icons'
import {
  faArrowRight,
  faBan,
  faCircleUserCirclePlus,
  faTriangleExclamation,
  faUpRightFromSquare
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { parseApiDate } from '~/api/utils'
import { Checkbox } from '~/components/Form'
import { HoverInfo } from '~/components/Global/Elements/HoverInfo/HoverInfo'
import { NameCircle } from '~/components/Global/Elements/NameCircle/NameCircle'
import { ScoreCircle } from '~/components/Global/Elements/ScoreCircle/ScoreCircle'
import { ActionsButtonList } from '~/domains/actions/components/ActionsButtonList/ActionsButtonList'
import { ApplicantCardHoverContent } from '~/domains/applicants/components/ApplicantCardHoverContent/ApplicantCardHoverContent'
import { Availability } from '~/domains/applicants/components/Availability/Availability'
import { useApplicantPicture } from '~/domains/applicants/queries/applicantPictures'
import { useApplicantSafetyCheck } from '~/domains/applicants/queries/applicantSafetyChecks'
import type {
  ApplicantHeadData,
  ApplicantMatchingData,
  AvailabilityData
} from '~/domains/applicants/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { useContracts } from '~/domains/contracts/queries/useContracts'
import { OrderTag } from '~/domains/orders/components/OrderTag/OrderTag'
import { orderTagFlushBottom } from '~/domains/orders/components/OrderTag/OrderTag.css'
import { ShortlistRankBadge } from '~/domains/orders/components/ShortlistRankBadge'
import type { OrderHead } from '~/domains/orders/types'
import { profilePicture40 } from '~/styles/picture.css'
import { vars } from '~/styles/theme.css'
import { formatRelativeTime } from '~/utils'
import { formatDateTime, formatTemporalShort } from '~/utils/date'
import { formatName } from '~/utils/names'
import {
  applicantCardContent,
  checkbox,
  checkboxFloating,
  contractDateRange,
  contractItem,
  contractItemContent,
  contractItemIcon,
  contractItemOpenIcon,
  contractName,
  contractsList,
  identity,
  identityPicture,
  matchingCircle,
  matchingCircleHidden,
  metaLine,
  name,
  nameText,
  nameWrapper,
  newApplicantBadge,
  safetyIcon,
  safetyIconHidden,
  updatedAt,
  updatedAtClickable,
  wrapper,
  wrapperMovable,
  wrapperSelected
} from './ApplicantCard.css'

export type ApplicantCardType = ApplicantHeadData & {
  created?: string
  updatedAt?: string
  note?: string
  availability?: AvailabilityData
} & ApplicantMatchingData

const ApplicantCardComponent = ({
  applicant,
  applicantId,
  selected,
  onSelect,
  disableActions = false,
  willChangeStatus = false,
  orderBanner,
  contextOrderId,
  dragOrderId,
  className,
  openApplicantDetail,
  showContracts = true,
  showSafetyCheck = true,
  showNewApplicantBanner = false,
  showScore = true,
  onOpenOrderHistory,
  skipEnterAnimation = false
}: {
  applicant?: ApplicantCardType
  applicantId?: number
  selected?: boolean
  onSelect?: () => void
  disableActions?: boolean
  willChangeStatus?: boolean
  showSafetyCheck?: boolean
  showNewApplicantBanner?: boolean
  showScore?: boolean
  orderBanner?: OrderHead
  contextOrderId?: number
  /** When set, included in drag data so the global kanban DnD handler knows which order to update */
  dragOrderId?: number
  className?: string
  openApplicantDetail: () => void
  showContracts?: boolean
  /** Opens order action history with this applicant pre-selected (e.g. from Kanban last-modified date). */
  onOpenOrderHistory?: () => void
  /** Skips the mount scale-in animation — used when mounting is driven by virtualized scroll, not a real addition. */
  skipEnterAnimation?: boolean
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  const [isHovered, setIsHovered] = useState(false)
  const { data: consideredNewDelay } = useConfig(
    'applicant.considered_new_delay'
  )
  const { data: applicantHeadPicture } = useApplicantPicture({
    applicantId: applicantId ?? applicant?.id
  })

  const localApplicantId = applicantId ?? applicant?.id

  const { data: contracts } = useContracts({
    applicantId: localApplicantId,
    orderId: contextOrderId,
    enabled: !!localApplicantId && !!contextOrderId && showContracts
  })

  const { data: safetyCheck } = useApplicantSafetyCheck({
    applicantId: localApplicantId,
    enabled: showSafetyCheck
  })

  if (!localApplicantId) {
    throw new Error('Applicant or applicantId is required')
  }

  const applicantHead = applicantHeadPicture?.applicant ?? applicant

  const rawPicUrl = applicantHeadPicture?.profilePicUrl
  const profilePicUrl =
    rawPicUrl && rawPicUrl.indexOf('/admin') !== -1
      ? `${legacyUrl ?? ''}${rawPicUrl}`
      : rawPicUrl

  useEffect(() => {
    const element = ref.current
    if (!element || disableActions || !applicant) return
    return draggable({
      element,
      getInitialData: (): ApplicantHeadData & { orderId?: number } => ({
        ...applicant,
        ...(dragOrderId !== undefined ? { orderId: dragOrderId } : {})
      })
    })
  }, [applicant, disableActions, dragOrderId])

  const derived = useMemo(() => {
    const filteredSafetyCheck = safetyCheck
      ? {
          ...safetyCheck,
          openOrders: safetyCheck.openOrders.filter(
            (order) => order.id !== contextOrderId
          )
        }
      : null

    const matchingPct = applicant?.matchingScorePercentage ?? 0

    const isNewApplicant =
      applicant?.created &&
      consideredNewDelay &&
      Date.now() - new Date(applicant.created).getTime() <=
        consideredNewDelay * 7 * 24 * 60 * 60 * 1000 // delay in weeks

    const hasExpiredDocuments =
      (filteredSafetyCheck?.expiredDocuments?.length ?? 0) > 0
    const hasMissingDocuments =
      (filteredSafetyCheck?.missingDocuments?.length ?? 0) > 0
    const hasOpenOrders = (filteredSafetyCheck?.openOrders?.length ?? 0) > 0
    const safetyDanger = hasMissingDocuments || hasExpiredDocuments
    const hasSafetyWarnings = safetyDanger || hasOpenOrders
    const updatedAtDate = applicant?.updatedAt
      ? new Date(applicant.updatedAt)
      : null
    const hasUpdatedAt =
      updatedAtDate !== null &&
      !Number.isNaN(updatedAtDate.getTime()) &&
      updatedAtDate.getFullYear() > 1970
    const formattedName = formatName(applicantHead)

    return {
      filteredSafetyCheck,
      matchingPct,
      isNewApplicant,
      safetyDanger,
      hasSafetyWarnings,
      hasUpdatedAt,
      formattedName
    }
  }, [
    safetyCheck,
    contextOrderId,
    applicantHead,
    applicant,
    consideredNewDelay
  ])

  if (!applicant) {
    return null
  }

  const {
    filteredSafetyCheck,
    matchingPct,
    isNewApplicant,
    safetyDanger,
    hasSafetyWarnings,
    hasUpdatedAt,
    formattedName
  } = derived

  return (
    <>
      <motion.div
        className={clsx(
          wrapper,
          !disableActions ? wrapperMovable : undefined,
          selected ? wrapperSelected : undefined,
          className
        )}
        ref={ref}
        initial={skipEnterAnimation ? false : { scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        onClick={onSelect && !disableActions ? () => onSelect() : undefined}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        {orderBanner && (
          <OrderTag order={orderBanner} className={orderTagFlushBottom} />
        )}
        <div className={applicantCardContent}>
          <div className={identity}>
            <div className={identityPicture}>
              {profilePicUrl ? (
                <img
                  src={profilePicUrl}
                  className={profilePicture40}
                  alt={formattedName}
                  draggable={false}
                />
              ) : (
                <NameCircle name={formattedName} size={40} />
              )}
              <Availability
                applicantId={localApplicantId}
                variant="embeddedDot"
                source="order_kanban_card"
              />
            </div>
            <div className={nameWrapper}>
              <h3
                className={name}
                onClick={(e) => {
                  e.stopPropagation()
                  openApplicantDetail()
                }}
                onAuxClick={(e) => {
                  e.stopPropagation()
                  window.open(
                    `${legacyUrl ?? ''}/admin/candidats/edit/${localApplicantId}`,
                    '_blank'
                  )
                }}
              >
                <span className={nameText}>{formattedName}</span>
                {showNewApplicantBanner && isNewApplicant && (
                  <span>
                    <FontAwesomeIcon
                      icon={faCircleUserCirclePlus}
                      className={newApplicantBadge}
                    />
                  </span>
                )}
              </h3>
              <div className={metaLine}>
                {hasUpdatedAt && onOpenOrderHistory && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenOrderHistory()
                    }}
                    className={clsx(updatedAtClickable, updatedAt)}
                    title={`Modifié le ${formatDateTime(new Date(applicant.updatedAt ?? ''))} — Voir l'historique`}
                  >
                    <FontAwesomeIcon icon={faClock} />
                    {formatRelativeTime(new Date(applicant.updatedAt ?? ''))}
                  </button>
                )}
                {hasUpdatedAt && !onOpenOrderHistory && (
                  <div
                    title={`Modifié le ${formatDateTime(new Date(applicant.updatedAt ?? ''))}`}
                    className={clsx(updatedAt)}
                  >
                    <FontAwesomeIcon icon={faClock} />
                    {formatRelativeTime(new Date(applicant.updatedAt ?? ''))}
                  </div>
                )}
                <ShortlistRankBadge
                  orderId={contextOrderId}
                  applicantId={localApplicantId}
                />
              </div>
            </div>
            {showSafetyCheck && filteredSafetyCheck && hasSafetyWarnings && (
              <FontAwesomeIcon
                icon={safetyDanger ? faBan : faTriangleExclamation}
                className={clsx(
                  safetyIcon,
                  isHovered &&
                    !selected &&
                    onSelect &&
                    !disableActions &&
                    safetyIconHidden
                )}
                style={
                  safetyDanger
                    ? { color: vars.color.danger }
                    : { color: vars.color.warning }
                }
              />
            )}
            {showScore && applicant.matchingScore !== undefined && (
              <ScoreCircle
                score={matchingPct}
                className={clsx(
                  matchingCircle,
                  (isHovered || selected) && matchingCircleHidden
                )}
              />
            )}
            {(isHovered || selected) && !disableActions && onSelect && (
              <motion.div
                initial={{ x: 10 }}
                animate={{ x: 0 }}
                exit={{ x: 10 }}
                className={clsx(
                  isHovered && !selected ? checkboxFloating : checkbox
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={selected}
                  onChange={onSelect ? () => onSelect() : undefined}
                  colorPalette={selected ? 'secondary' : 'medium'}
                />
              </motion.div>
            )}
          </div>
          {showContracts && contracts && contracts.length > 0 && !selected && (
            <div className={contractsList}>
              {contracts.map((contract) => {
                const startDate = formatTemporalShort(
                  parseApiDate(contract.contractStart),
                  ''
                )
                const endDate = parseApiDate(contract.contractEnd)
                const dateRange = endDate ? (
                  <>
                    {startDate} <FontAwesomeIcon icon={faArrowRight} />{' '}
                    {formatTemporalShort(endDate, '')}
                  </>
                ) : (
                  `À partir du ${startDate}`
                )

                return (
                  <a
                    href={`${legacyUrl ?? ''}/admin/contracts/modifier/${contract.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    key={contract.id}
                    className={contractItem}
                  >
                    <FontAwesomeIcon
                      icon={faFileContract}
                      className={contractItemIcon}
                    />
                    <span className={contractItemContent}>
                      <span className={contractName}>
                        {contract.contractType.name}
                      </span>
                      <span className={contractDateRange}>{dateRange}</span>
                    </span>
                    <span className={contractItemOpenIcon}>
                      <FontAwesomeIcon icon={faUpRightFromSquare} />
                    </span>
                  </a>
                )
              })}
            </div>
          )}
          {!selected && !disableActions && (
            <ActionsButtonList
              target={{ type: 'applicant', ids: [localApplicantId] }}
              contextOrder={
                contextOrderId
                  ? {
                      id: contextOrderId,
                      willChangeStatus: willChangeStatus
                    }
                  : undefined
              }
              hiddenActions={['addToOrder']}
              hasEmail={applicant !== undefined ? !!applicant.email : true}
            />
          )}
        </div>
      </motion.div>
      <HoverInfo linkedRef={ref.current}>
        <ApplicantCardHoverContent
          applicant={applicant}
          contextOrderId={contextOrderId}
          showNewApplicantBanner={showNewApplicantBanner}
          showScore={showScore}
          showSafetyCheck={showSafetyCheck}
        />
      </HoverInfo>
    </>
  )
}

export const ApplicantCard = memo(ApplicantCardComponent)
